import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { appendGroupIdParam, applyAnalyticsPendingLoading, markAnalyticsFetchKey } from '../../utils/analyticsQuery';
import dayjs from 'dayjs';

export const getCsatAnalyticsStats = createAsyncThunk(
  'csatAnalyticsStats/getCsatAnalyticsStats',
  async ({ range, startDate, endDate, group_id }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      if (!businessId) {
        throw new Error('Business ID not found');
      }

      let url = `${ROOT_URL}/analytics/csat/${businessId}`;
      let params = new URLSearchParams();
      
      params.append('timezone', timezone);
      
      if (range === 'custom' && startDate && endDate) {
        params.append('range', 'custom');
        params.append('from', startDate);
        params.append('to', endDate);
      } else {
        const apiRange = range === '365d' ? '1y' : range;
        params.append('range', apiRange);
      }

      appendGroupIdParam(params, group_id);
      
      url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching CSAT analytics stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch CSAT analytics stats'
      );
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  metrics: {
    good: { count: 0, percentage: 0 },
    bad: { count: 0, percentage: 0 },
    totalFeedbacks: 0
  },
  range: null,
  from: null,
  to: null
};

// Create slice
const csatAnalyticsStatsSlice = createSlice({
  name: 'csatAnalyticsStats',
  initialState,
  reducers: {
    resetCsatAnalyticsStats: (state) => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCsatAnalyticsStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getCsatAnalyticsStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        state.data = action.payload.data;
        state.metrics = action.payload.data.metrics;
        state.range = action.payload.data.range;
        state.from = action.payload.data.from;
        state.to = action.payload.data.to;
      })
      .addCase(getCsatAnalyticsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
      });
  }
});

// Export actions
export const { resetCsatAnalyticsStats, clearError } = csatAnalyticsStatsSlice.actions;

// Export reducer
export default csatAnalyticsStatsSlice.reducer;
